import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { Layout } from '../components/layout/Layout';
import { MATCHING_DECKS, MATCH_TYPE_LABEL, MATCH_TYPES } from '../data/matching';
import { getUserMatchingDecks, deleteUserMatchingDeck } from '../services/firestoreService';
import { FiPlus, FiMoreVertical, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { useTranslation } from '../i18n/useTranslation';
import '../styles/sequencing.css';
import '../styles/deck-library.css';
import '../styles/matching.css';

export const MatchingLibraryPage = () => {
  const navigate     = useNavigate();
  const { currentUser } = useAuth();
  const { t, tc } = useTranslation();

  const [filterCat,   setFilterCat]   = useState('All');
  const [search,       setSearch]      = useState('');
  const [showFavOnly,  setShowFavOnly] = useState(false);

  const [publishedDecks, setPublishedDecks] = useState([]);
  const [loadingDecks,   setLoadingDecks]   = useState(true);
  const [decksError,     setDecksError]     = useState(false);

  const [userDecks, setUserDecks]               = useState([]);
  const [userDecksLoading, setUserDecksLoading] = useState(true);
  const [openDeckMenu, setOpenDeckMenu]         = useState(null);
  const [confirmDelete, setConfirmDelete]       = useState(null);
  const [deleting, setDeleting]                 = useState(false);

  const [favoriteIds, setFavoriteIds] = useState(new Set());

  // ── Load all published activities (admin-curated content) ───────────────
  useEffect(() => {
    getDocs(
      query(
        collection(db, 'generatedMatchingDecks'),
        where('status', '==', 'published'),
      )
    )
      .then(snap => {
        const decks = snap.docs
          .map(d => ({ id: d.id, _source: 'generated', ...d.data() }))
          .sort((a, b) => (b.publishedAt?.seconds || 0) - (a.publishedAt?.seconds || 0));
        setPublishedDecks(decks);
        setLoadingDecks(false);
      })
      .catch(err => {
        console.error('Error loading published matching activities:', err);
        setDecksError(true);
        setLoadingDecks(false);
      });
  }, []);

  // ── Load the current user's own custom matching activities ───────────────
  useEffect(() => {
    if (!currentUser?.uid) { setUserDecksLoading(false); return; }
    getUserMatchingDecks(currentUser.uid).then(res => {
      setUserDecks(res.success ? res.data : []);
      setUserDecksLoading(false);
    });
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!openDeckMenu) return;
    const close = () => setOpenDeckMenu(null);
    const id = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(id); document.removeEventListener('click', close); };
  }, [openDeckMenu]);

  const handleDeleteConfirmed = async () => {
    if (deleting || !confirmDelete) return;
    setDeleting(true);
    await deleteUserMatchingDeck(confirmDelete.id);
    setUserDecks(prev => prev.filter(d => d.id !== confirmDelete.id));
    setConfirmDelete(null);
    setDeleting(false);
  };

  // ── Subscribe to the user's favorites ─────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.uid) return;
    return onSnapshot(
      collection(db, 'users', currentUser.uid, 'favorites'),
      snap => setFavoriteIds(new Set(snap.docs.map(d => d.id))),
      err => console.error('Error loading favorites:', err),
    );
  }, [currentUser?.uid]);

  const toggleFavorite = async (e, deckId, meta) => {
    e.stopPropagation();
    if (!currentUser?.uid) return;
    const ref = doc(db, 'users', currentUser.uid, 'favorites', deckId);
    try {
      if (favoriteIds.has(deckId)) {
        await deleteDoc(ref);
      } else {
        await setDoc(ref, { ...meta, addedAt: new Date() });
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  // ── Combine published + static + the user's own decks into one searchable list ──
  const combined = [
    ...userDecks.map(d => ({
      id: d.id,
      title: d.title,
      category: null,
      matchType: null,
      trialCount: d.trials?.length ?? d.trialCount ?? 0,
      thumbnail: d.trials?.[0]?.prompt?.imageUrl || null,
      route: `/matching/user/${d.id}`,
      source: 'userCustom',
    })),
    ...publishedDecks.map(d => ({
      id: d.id,
      title: d.title,
      category: d.category,
      matchType: d.matchType,
      trialCount: d.trials != null ? d.trials.length : (d.trialCount ?? 0),
      thumbnail: d.trials?.[0]?.prompt?.imageUrl || null,
      route: `/matching/generated/${d.id}`,
      source: 'generated',
    })),
    ...MATCHING_DECKS.map(d => ({
      id: d.id,
      title: d.title,
      category: d.category,
      matchType: d.matchType,
      trialCount: d.trials?.length || 0,
      color: d.color,
      emoji: d.emoji,
      route: `/matching/${d.id}`,
      source: 'static',
    })),
  ];

  const categories = ['All', ...new Set(combined.map(d => d.category).filter(Boolean))];

  const filtered = combined.filter(d => {
    if (filterCat !== 'All' && d.category !== filterCat) return false;
    if (showFavOnly && !favoriteIds.has(d.id)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (d.title ?? '').toLowerCase().includes(q) || (d.category ?? '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <Layout>
      <div style={{ padding: '0 0 60px' }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px' }}>
            {t('matchLib.title')}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
            {t('matchLib.subtitle')}
          </p>
        </div>

        {/* ── Search ─────────────────────────────────────────────────────── */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('matchLib.searchPlaceholder')}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '11px 14px 11px 38px',
              fontSize: 14, fontFamily: 'inherit', border: '1.5px solid var(--border)',
              borderRadius: 12, background: 'var(--surface)', color: 'var(--text-primary)', outline: 'none',
            }}
          />
        </div>

        {/* ── Filters ────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24, alignItems: 'center' }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCat(cat)}
              style={{
                padding: '5px 14px', borderRadius: 20, border: '1.5px solid',
                borderColor: filterCat === cat ? 'var(--brand)' : 'var(--border)',
                background:  filterCat === cat ? 'var(--brand)' : 'var(--surface)',
                color:       filterCat === cat ? '#fff' : 'var(--text-secondary)',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', transition: 'all 0.12s',
              }}
            >
              {cat === 'All' ? t('matchLib.all') : tc(cat)}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button
            onClick={() => setShowFavOnly(f => !f)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 14px', borderRadius: 20, border: '1.5px solid',
              borderColor: showFavOnly ? '#F59E0B' : 'var(--border)',
              background:  showFavOnly ? '#FFFBEB' : 'var(--surface)',
              color:       showFavOnly ? '#92400E' : 'var(--text-secondary)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {showFavOnly ? '★' : '☆'} {t('matchLib.favorites')}
          </button>
          <button
            onClick={() => navigate('/matching/create')}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 14px', borderRadius: 20, border: 'none',
              background: 'var(--brand)', color: '#fff',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <FiPlus size={14} /> {t('matchLib.newActivity')}
          </button>
        </div>

        {/* ── Delete confirmation modal ─────────────────────────────────── */}
        {confirmDelete && (
          <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
            onClick={() => setConfirmDelete(null)}
          >
            <div style={{ backgroundColor:'#FFFFFF', borderRadius:16, padding:'28px 24px', maxWidth:340, width:'100%', boxShadow:'0 24px 64px rgba(0,0,0,0.45)', border:'1.5px solid #E5E7EB' }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ fontSize:17, fontWeight:700, color:'var(--text-primary)', marginBottom:8 }}>{t('dash.deleteTitle')} "{confirmDelete.title}"?</h3>
              <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:24, lineHeight:1.5 }}>{t('matchLib.deleteBody')}</p>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setConfirmDelete(null)} style={{ flex:1, height:42, background:'var(--bg)', border:'1.5px solid var(--border)', borderRadius:10, fontSize:14, fontWeight:600, color:'var(--text-secondary)', cursor:'pointer', fontFamily:'inherit' }}>{t('dash.cancel')}</button>
                <button onClick={handleDeleteConfirmed} disabled={deleting} style={{ flex:1, height:42, background:'#DC2626', border:'none', borderRadius:10, fontSize:14, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'inherit', opacity: deleting ? 0.6 : 1 }}>
                  {deleting ? t('dash.deleting') : t('dash.deleteTitle')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Activity grid ──────────────────────────────────────────────── */}
        {decksError && (
          <div style={{
            background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 12,
            padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#991B1B',
          }}>
            Could not load activities — check your connection and refresh.
          </div>
        )}

        {(loadingDecks || userDecksLoading) ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
            {t('matchLib.loadingActivities')}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '60px 20px', fontSize: 14 }}>
            {showFavOnly ? t('matchLib.noFavorites') : t('matchLib.noMatches')}
          </div>
        ) : (
          <div className="match-lib-grid">
            {filtered.map(deck => {
              const isFav = favoriteIds.has(deck.id);

              return (
                <div
                  key={deck.id}
                  className="match-lib-card"
                  onClick={() => navigate(deck.route)}
                >
                  {/* Card face */}
                  <div className="match-lib-face" style={{ background: deck.thumbnail ? '#F3F4F6' : (deck.color || 'linear-gradient(135deg, #8B2635 0%, #C0555E 100%)') }}>
                    {deck.thumbnail
                      ? <img src={deck.thumbnail} alt={deck.title} />
                      : <span style={{ fontSize: 44 }}>{deck.emoji || (deck.matchType === MATCH_TYPES.SOUND ? '🔊' : '🧩')}</span>
                    }

                    {/* Favorite star */}
                    <button
                      onClick={e => toggleFavorite(e, deck.id, {
                        title: deck.title, category: deck.category,
                      })}
                      style={{
                        position: 'absolute', top: 8, right: 8, zIndex: 2,
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.92)', border: 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 15, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                        color: isFav ? '#F59E0B' : '#D1D5DB',
                      }}
                    >
                      {isFav ? '★' : '☆'}
                    </button>

                    {/* Manage menu — only for the user's own custom activities */}
                    {deck.source === 'userCustom' && (
                      <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={e => { e.stopPropagation(); setOpenDeckMenu(prev => prev === deck.id ? null : deck.id); }}
                          title={t('matchLib.options')}
                          style={{
                            width: 28, height: 28, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.92)', border: 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', color: 'var(--text-secondary)',
                          }}
                        >
                          <FiMoreVertical size={15} />
                        </button>
                        {openDeckMenu === deck.id && (
                          <div className="browse-deck-menu" onClick={e => e.stopPropagation()}>
                            <button className="browse-menu-item" onClick={() => { setOpenDeckMenu(null); navigate('/matching/create', { state: { editDeckId: deck.id } }); }}>
                              <FiEdit2 size={13} /> {t('dash.edit')}
                            </button>
                            <button className="browse-menu-item browse-menu-item--danger" onClick={() => { setOpenDeckMenu(null); setConfirmDelete({ id: deck.id, title: deck.title }); }}>
                              <FiTrash2 size={13} /> {t('dash.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="match-lib-info">
                    <div className="match-lib-name">{deck.source === 'generated' || deck.source === 'userCustom' ? deck.title : tc(deck.title)}</div>
                    <div className="match-lib-meta">
                      {deck.trialCount} {t('match.trials')}
                      {deck.source === 'userCustom' && ` · ${t('matchLib.mine')}`}
                      {deck.category && ` · ${deck.source === 'generated' ? deck.category : tc(deck.category)}`}
                    </div>
                    {deck.matchType && (
                      <span className="match-lib-badge">{MATCH_TYPE_LABEL[deck.matchType] || deck.matchType}</span>
                    )}
                  </div>
                  <button
                    className="match-lib-start-btn"
                    onClick={e => { e.stopPropagation(); navigate(deck.route); }}
                  >
                    {t('matchLib.startActivity')}
                  </button>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </Layout>
  );
};
